import { CONSOLE_PRODUCT, type NavItemId } from '@ctcells/core'
import type { ReactNode } from 'react'

/**
 * 내비 아이콘 — 두 벌의 격자가 섞여 있다.
 *
 * - **16 격자 · 선(stroke)** — 헤더 다섯(모니터링 · 작업 관리 · 실험 준비 · 알람 · 관리). 디자인팀 시안
 *   `docs/design/CTcells-console.html` 의 아이콘 집합 `I`(`mon` · `job` · `prep` · `bell` · `gear`)를
 *   **경로 그대로** 옮겼다(2026-10-01). 획 1.6 · 둥근 끝·모서리도 시안 값이다 — 손으로 다듬지 마라.
 * - **24 격자 · 면(fill)** — 나머지(이력 · 시료 · 레시피 · 랩웨어 · 모듈 · 로그). 와이어프레임
 *   `NAV_ICON`(1090행)을 옮긴 것으로 장비 관점 사이드바(`ConsoleNav`)만 쓴다.
 *
 * 🔴 **크기는 CSS 가 정한다**(`.tabnav__item svg` · `.nav__item svg` — `--icon-*` 토큰). 격자가 달라도
 *    `viewBox` 만 다르고 그려지는 크기는 같다 — 두 벌이 한 줄에 서도 키가 맞는다.
 *
 * 원자 `Icon` 과 따로 두는 이유: 저쪽은 버튼 안에 들어가는 동작 아이콘(재생·정지·복구)이고
 * 이쪽은 내비 전용이라 쓰는 곳이 하나다. 합치면 두 집합의 크기·획 규칙이 섞인다.
 */
interface NavGlyph {
  /** 16 = 시안 선 아이콘 · 24 = 와이어프레임 면 아이콘 */
  grid: 16 | 24
  node: ReactNode
}

/** 시안 `I.gear` — 관리 · 설정(계정 메뉴) 둘이 같은 톱니를 쓴다 */
const GEAR: NavGlyph = {
  grid: 16,
  node: (
    <>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.5v1.6M8 12.9v1.6M1.5 8h1.6M12.9 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M3.4 12.6l1.1-1.1M11.5 4.5l1.1-1.1" />
    </>
  ),
}

const PATHS: Record<NavItemId, NavGlyph> = {
  /*
   * 모니터 — 화면 + 받침(eric 2026-10-07 「모니터링 아이콘을 컴퓨터 그 모니터 아이콘으로 바꿔줘」).
   * ⚠ 시안 `I.mon`(타일 넷 · 대시보드)은 eric 지시로 바꿨다 — 같은 16 격자 · 획 1.6 · 둥근 끝 규칙은 그대로.
   */
  monitoring: {
    grid: 16,
    node: (
      <>
        <rect x="1.5" y="2.5" width="13" height="8.5" rx="1.4" />
        <path d="M8 11v2.5M5 13.5h6" />
      </>
    ),
  },
  /* 시안 `I.job` — 상자 안의 표(행 둘 · 열 하나) */
  batches: {
    grid: 16,
    node: (
      <>
        <rect x="1.5" y="1.5" width="13" height="13" rx="2" />
        <path d="M1.5 6h13M1.5 10.5h13M6 1.5v13" />
      </>
    ),
  },
  batchHistory: {
    grid: 24,
    node: (
      <>
        <path
          d="M4 5h16M4 12h10M4 19h16"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <circle cx="18" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
      </>
    ),
  },
  /* 시안 `I.bell` — 종 */
  alarms: {
    grid: 16,
    node: (
      <>
        <path d="M4 11V7a4 4 0 0 1 8 0v4l1.2 1.5H2.8Z" />
        <path d="M6.5 14.2h3" />
      </>
    ),
  },
  /*
   * 「실험 준비」 — 시료·레시피·랩웨어를 품는 무리(2026-09-18 · 이름은 2026-09-21 「자원」→「준비」,
   * 2026-10-01 시안을 따라 「실험 준비」). 시안 `I.prep` — 삼각 플라스크. 세 탭 중 하나의 아이콘을 쓰면
   * 그 화면처럼 보이므로 따로 둔다. `samples` 등은 장비 관점 사이드바에 남아 계속 쓰인다.
   */
  resources: {
    grid: 16,
    node: (
      <>
        <path d="M6 1.5h4M6.8 1.5v4.2L3 12.6a1.3 1.3 0 0 0 1.1 1.9h7.8a1.3 1.3 0 0 0 1.1-1.9L9.2 5.7V1.5" />
        <path d="M4.6 9.8h6.8" />
      </>
    ),
  },
  samples: {
    grid: 24,
    node: (
      <>
        <path
          d="M12 3c0 0-5 6.2-5 10a5 5 0 0 0 10 0c0-3.8-5-10-5-10z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinejoin="round"
        />
        <path
          d="M9.5 14.5a2.5 2.5 0 0 0 2.5 2.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </>
    ),
  },
  recipes: {
    grid: 24,
    node: (
      <>
        <path
          d="M5 3h11l3 3v15H5z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinejoin="round"
        />
        <path
          d="M8 9h8M8 13h8M8 17h5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </>
    ),
  },
  modules: {
    grid: 24,
    node: (
      <>
        <rect
          x="3"
          y="4"
          width="18"
          height="7"
          rx="1.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
        />
        <rect
          x="3"
          y="13"
          width="18"
          height="7"
          rx="1.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
        />
        <circle cx="7" cy="7.5" r="1.3" />
        <circle cx="7" cy="16.5" r="1.3" />
      </>
    ),
  },
  logs: {
    grid: 24,
    node: (
      <>
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.2" />
        <path
          d="M12 7v5l3.5 2.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  /* 시안 `I.gear` — 톱니 */
  admin: GEAR,
  account: GEAR,
}

/** 내비 아이콘. `aria-hidden` 이다 — 이름은 옆의 라벨이 쥔다. */
export function NavIcon({ id }: { id: NavItemId }) {
  const glyph = PATHS[id]
  if (glyph.grid === 16) {
    /* 시안 래퍼 그대로 — `fill="none" stroke="currentColor" stroke-width="1.6"` · 둥근 끝·모서리 */
    return (
      <svg
        aria-hidden="true"
        fill="none"
        focusable="false"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
        viewBox="0 0 16 16"
      >
        {glyph.node}
      </svg>
    )
  }
  return (
    <svg aria-hidden="true" fill="currentColor" focusable="false" viewBox="0 0 24 24">
      {glyph.node}
    </svg>
  )
}

/**
 * CTCells 워드마크 — 통합 콘솔의 브랜드 표기(헤더 · 로그인 카드).
 *
 * 🔴 **SVG 인 까닭은 «색이 테마를 따라야» 해서다**(eric 지시 2026-09-23 「그럼 저거 SVG 로 바꿔서
 *    수정해 주면 되잖아」). 2026-09-23 오전까지는 PNG(`assets/ctcells-wordmark.png`)였는데,
 *    비트맵은 잉크가 자산 안에 구워져 있어 **다크 테마에서 짙은 남색이 어두운 바탕에 가라앉았다.**
 *    `<img src="…svg">` 로 바꿔도 같다 — 바깥 문서의 CSS 가 그 안에 못 닿는다.
 *    ⇒ **인라인 SVG + `currentColor`** 여야 `color` 한 줄로 테마를 탄다(`BrandLogo` 와 같은 방식).
 * 🔴 **색은 쓰는 쪽이 `--color-brand` 로 준다**(`shell.css` · `resource.css`).
 *    라이트는 자산의 실측 잉크(남색), 다크는 밝은 단이다 — 값은 `tokens/palette.css` 한 곳.
 * 🔴 **모양은 eric 자산 그대로다.** PNG 의 알파 채널을 12배로 키워 임계한 뒤 potrace 로 딴
 *    윤곽이고(2026-09-23 · 원본과 겹침 99.25%), `viewBox` 는 원본 픽셀 크기(188×39) 그대로다.
 *    ⚠ 글자 모양을 손으로 고치지 마라 — 새 자산이 오면 같은 절차로 다시 딴다.
 *    ⚠ **예외 하나 — 마지막 S 서브패스만 손으로 다시 맞춘 곡선이다**(eric 2026-11-02 「마지막 s가 도트가 뭔가
 *    부자연스러운데」). 자산이 S 의 오른쪽 · 아래에서 잘려(x=188 · y=39) 그 자리가 평평한 면과 혹으로 땄다.
 *    같은 외곽 · 같은 범위로 큐빅 16개 + 직선 2개로 옮겼다. 다른 글자는 추적 윤곽 그대로다.
 * 🔴 **접근 이름은 제품명 하나**(`CONSOLE_PRODUCT`). 그림이 글자를 대신하므로 `aria-hidden` 이
 *    아니라 `role="img"` + `aria-label` 이다. ROBOS(`BrandLogo`)와 갈리는 지점이다 —
 *    저쪽은 옆에 글자가 따로 서서 그림이 숨는다.
 */
export function CtcellsMark({
  className,
  label = CONSOLE_PRODUCT,
}: {
  className?: string | undefined
  /** 접근 이름 — 모듈 앱은 제 제품명(`MODULES[키].product`)을 넘긴다. 기본은 통합 콘솔 */
  label?: string | undefined
}) {
  return (
    <svg aria-label={label} className={className} focusable="false" role="img" viewBox="0 0 188 39">
      <path
        d="M 41 0.3 C 40.9 0.6 40.7 0.7 40 1 L 39.5 1.2 38.8 1.8 L 38.2 2.4 38 2.7 C 37.8 2.9 37.5 3.2 37.3 3.5 L 36.9 3.9 36.4 4 L 36 4.2 35.2 4.7 L 34.4 5.2 33.8 5.9 L 33.2 6.5 33.1 6.8 L 33 7.2 33 7.5 L 33.1 7.8 33.3 7.9 L 33.5 8.1 34 8.1 C 34.3 8 34.7 8 35 8 L 35.4 8 35.9 7.6 L 36.4 7.1 37.3 6.8 L 38.2 6.5 38.6 6.5 L 39 6.5 39.7 6.7 C 40.8 7 40.9 7.1 41.3 7.5 L 41.7 7.8 42 7.9 L 42.3 8 43.3 8 L 44.4 8 44.6 7.8 C 44.8 7.7 44.9 7.6 44.9 7.6 C 44.9 7.4 45.6 7.1 46.4 6.8 L 47.3 6.5 47.6 6.5 L 48 6.5 48.9 6.8 L 49.9 7 50.3 7.4 L 50.7 7.8 51 7.9 C 51.2 8 51.7 8 52.1 8 L 53 8 53.2 7.8 L 53.4 7.7 53.4 7.5 C 53.4 7.4 53.3 7 53.2 6.8 L 53 6.3 52.4 5.7 C 52.1 5.4 51.5 4.9 51.1 4.6 L 50.4 4.2 49.9 4 L 49.3 3.8 48.9 3.3 C 48.7 3 48.3 2.5 48.1 2.3 C 47.7 1.7 47 1.2 46.4 1 C 45.7 0.7 45.5 0.6 45.4 0.3 L 45.2 0 43.2 0 L 41.2-0 41 0.3 M 41.9 3 C 41.7 3.1 41.4 3.2 41.3 3.3 L 41.2 3.4 41.2 3.7 L 41.2 3.9 41.6 4.3 L 41.9 4.7 42.4 4.9 L 42.9 5.1 43.3 5.1 L 43.7 5.1 44.2 4.8 C 44.5 4.6 44.8 4.3 44.9 4.2 L 45.2 3.9 45.2 3.7 C 45.2 3.6 45.1 3.4 45 3.3 L 44.8 3.1 44.4 3 C 44.2 3 43.6 2.9 43.1 2.9 L 42.3 2.9 41.9 3 M 2 5.5 L 1.5 5.7 1 6.3 C 0.8 6.5 0.4 6.8 0.3 6.9 L 0 7 0 8.3 L 0 9.6 0.3 9.7 L 0.7 9.8 1.1 10.3 L 1.6 10.8 1.9 10.9 L 2.2 11 2.9 11 L 3.6 11 4 10.9 C 4.9 10.4 5.8 9.1 5.8 8.1 L 5.8 7.7 5.7 7.5 C 5.5 7.2 5.1 6.5 4.9 6.3 C 4.8 6.2 4.4 5.9 4 5.7 L 3.2 5.3 2.9 5.3 L 2.5 5.3 2 5.5 M 176.3 7.9 C 170.87 7.9 165.3 10.97 165.3 17 C 165.3 22.24 168.62 24.42 173.2 25.75 C 175.49 26.41 181 27.32 181 30 C 181 32.05 177.95 32.95 176.4 32.95 C 174.4 32.95 172.3 31.92 171.65 29.75 C 171.51 29.29 170.76 28.5 170.3 28.5 L 165.8 28.5 C 165.52 28.5 164.44 28.99 164.5 29.6 C 164.67 31.32 166.21 39 176.35 39 C 183.11 39 188 35.3 188 29.25 C 188 22.05 180.95 20.71 180.4 20.55 C 178.64 20.04 172.2 18.67 172.2 16.7 C 172.2 15.08 174.18 14.1 175.9 14.1 C 177.65 14.1 179.44 14.7 180.05 16.45 C 180.28 17.1 180.66 17.6 181.4 17.6 L 186.1 17.6 C 186.46 17.6 187.02 17 187 16.5 C 186.72 10.9 181.52 7.9 176.3 7.9 M 14.1 8.3 C 13.8 8.3 13.3 8.4 13 8.5 C 12.7 8.6 11.9 8.8 11.3 9 L 10.1 9.3 9.6 9.5 C 9.3 9.7 8.9 9.9 8.6 10 C 8.3 10.1 8 10.3 7.8 10.5 C 7.7 10.6 7.3 10.8 7.1 11 C 5.9 11.6 3.5 14.1 3 15 C 2.9 15.2 2.7 15.6 2.5 15.8 C 2.3 16.1 2.1 16.5 2 16.7 C 2 16.9 1.8 17.4 1.6 17.7 L 1.3 18.4 1.1 19 C 1.1 19.4 0.9 20.4 0.8 21.5 L 0.6 23.3 0.7 24.5 C 0.8 25.2 0.9 26.3 1 27 L 1.2 28.1 1.6 28.9 C 1.7 29.3 2 29.8 2 30 C 2.1 30.2 2.3 30.6 2.5 30.8 C 2.6 31 2.9 31.3 3 31.5 C 3.1 31.7 3.3 32.1 3.5 32.3 C 3.7 32.5 4 32.9 4.2 33.2 C 4.7 33.7 5.9 34.9 6.4 35.2 C 6.6 35.4 6.9 35.5 6.9 35.6 C 7 35.7 7.3 35.9 7.5 36 C 7.8 36.1 8.2 36.4 8.4 36.6 C 8.6 36.7 9 36.9 9.3 37 C 9.6 37.1 10.1 37.3 10.5 37.5 C 12.5 38.3 17.1 38.5 20.2 37.8 L 21 37.7 21.6 37.4 C 22 37.2 22.4 37.1 22.6 37 C 22.8 36.9 23.2 36.7 23.5 36.5 C 23.8 36.3 24.2 36.1 24.4 36 C 24.6 35.9 24.9 35.7 25 35.6 C 25.1 35.5 25.3 35.3 25.5 35.1 C 26.1 34.7 27.8 32.9 28.1 32.5 C 28.1 32.3 28.3 32 28.5 31.8 C 29.2 31 29.3 30.6 29 30 L 28.9 29.7 28.5 29.5 C 28.2 29.3 27.8 29.1 27.5 29 C 27.2 28.9 26.7 28.7 26.4 28.5 C 26.1 28.3 25.7 28.1 25.4 28 C 25.2 27.9 24.8 27.7 24.5 27.5 C 23.8 27 23.3 26.9 22.8 27.1 L 22.5 27.3 22.1 27.9 L 21.6 28.6 20.9 29.2 L 20.2 29.8 19.9 30 C 19.8 30 19.3 30.2 19 30.4 L 18.3 30.8 17.9 30.8 C 17.6 30.9 17.1 31 16.7 31 L 15.9 31.1 15.1 31 C 13.9 30.9 13.6 30.8 12.5 30.2 L 11.5 29.7 11.1 29.4 C 10.5 28.9 10 28.5 9.6 27.9 L 9.2 27.4 8.9 26.8 C 8.8 26.5 8.6 26.1 8.5 26 C 8.4 25.8 8.3 25.5 8.2 25.2 L 8 24.6 8.1 23.3 L 8.1 22 8.2 21.5 C 8.3 21.2 8.4 20.8 8.6 20.5 C 8.7 20.3 8.9 20 9 19.7 L 9.1 19.4 9.5 18.8 C 10.1 18.1 11.1 17.2 11.5 17 C 11.7 16.9 12.1 16.6 12.3 16.5 L 12.7 16.2 13.5 16 C 13.9 15.9 14.6 15.8 15 15.7 L 15.8 15.6 16.7 15.7 C 17.2 15.8 17.9 15.9 18.3 16 L 19 16.2 19.4 16.5 C 19.7 16.7 20 16.9 20.2 17 L 20.6 17.2 21.2 17.8 C 21.5 18.1 21.9 18.6 22 18.7 C 22.2 19.1 22.4 19.3 22.8 19.4 L 23.1 19.4 24 19.1 C 24.5 18.9 25.1 18.6 25.3 18.4 C 25.5 18.3 26 18.1 26.3 18 C 26.6 17.8 27 17.6 27.1 17.5 C 27.3 17.4 27.7 17.2 28 17 C 28.4 16.9 28.7 16.7 28.8 16.6 L 29 16.4 29 16 L 28.9 15.5 28.7 15.2 C 28.5 15 28.3 14.7 28.2 14.6 C 27.7 13.8 26.5 12.5 25.9 12 C 25.6 11.7 25.2 11.4 25.1 11.3 C 25 11.2 24.8 11.1 24.7 11 C 24.5 10.9 24.2 10.7 24 10.6 C 23.8 10.4 23.1 10 22.5 9.7 L 21.4 9.2 20.6 9 C 20.2 8.9 19.5 8.7 19 8.6 C 17.7 8.2 15.7 8.1 14.1 8.3 M 70.7 8.2 C 70.3 8.3 68.6 8.7 67.2 9.1 C 66.9 9.2 66.5 9.4 66.3 9.5 C 66.1 9.6 65.7 9.8 65.4 9.9 L 64.8 10.2 64.4 10.5 C 64.2 10.6 63.9 10.9 63.7 11 C 63.5 11.1 63.2 11.3 63 11.4 C 62.9 11.5 62.6 11.7 62.5 11.9 C 61.4 12.7 59.3 15.1 58.9 16.3 C 58.8 16.6 58.6 16.9 58.6 16.9 C 58.4 17 58.1 17.7 57.9 18.5 C 57.8 18.9 57.6 19.5 57.5 19.8 L 57.3 20.4 57.2 21.1 L 57.1 21.9 57.1 23.4 L 57.1 24.9 57.2 25.6 L 57.3 26.3 57.5 27 C 57.6 27.3 57.8 28 58 28.4 L 58.2 29.2 58.4 29.6 C 58.6 29.9 58.9 30.3 59 30.6 C 59.1 30.9 59.4 31.3 59.5 31.5 C 59.7 31.7 59.9 32.1 60.1 32.3 L 60.4 32.8 61.4 33.8 C 61.9 34.3 62.5 34.9 62.7 35 C 62.9 35.1 63.2 35.3 63.4 35.5 C 63.6 35.7 63.9 35.9 64.2 36.1 C 64.5 36.2 64.8 36.4 65 36.6 C 65.2 36.7 66.5 37.3 67.6 37.7 L 68 37.8 69.4 38 L 70.8 38.2 72.5 38.2 L 74.3 38.2 75.7 38 L 77 37.8 77.5 37.7 C 78.4 37.4 79.9 36.7 80.1 36.5 C 80.2 36.4 80.5 36.2 80.8 36.1 C 81.1 35.9 81.5 35.7 81.5 35.6 C 81.6 35.5 81.9 35.3 82.1 35.1 C 82.9 34.5 84.6 32.7 84.9 32 C 85 31.9 85.2 31.5 85.4 31.3 C 85.6 31.1 85.8 30.8 85.8 30.7 L 85.9 30.4 85.8 30.2 C 85.8 30.1 85.5 29.8 85.3 29.6 L 84.9 29.3 84.1 29 C 83.7 28.9 83.2 28.6 83 28.5 C 82.8 28.4 82.4 28.2 82.1 28 C 81.8 27.9 81.4 27.7 81.3 27.6 C 80.8 27.3 80.4 27.1 80.1 27 L 79.8 26.9 79.5 27 L 79.2 27.1 79 27.4 C 78.7 27.9 78.2 28.6 77.5 29.2 L 77 29.7 75.9 30.3 L 74.9 30.8 74.2 30.9 L 73.5 31 72.1 31 L 70.8 30.9 70.3 30.8 C 70 30.7 69.7 30.6 69.5 30.5 C 69.3 30.4 69 30.2 68.7 30.1 L 68.2 29.8 67.7 29.5 C 67.5 29.3 67 28.8 66.7 28.5 L 66.1 27.9 65.9 27.5 C 65.7 27.2 65.6 27 65.5 26.9 C 65.2 26.6 65 26 64.8 25 L 64.7 24 64.7 23.3 L 64.7 22.6 64.8 21.7 C 65 20.6 65.1 20.4 65.5 19.8 C 65.7 19.6 65.9 19.2 66.1 18.9 L 66.3 18.5 67 17.8 C 67.4 17.5 68 17 68.3 16.9 C 68.6 16.7 68.9 16.5 69.1 16.4 L 69.5 16.2 70.4 15.9 L 71.4 15.7 72.4 15.7 L 73.5 15.7 74.2 15.9 C 75.1 16 75.6 16.2 76 16.5 C 76.1 16.6 76.4 16.7 76.6 16.8 C 77 17.1 77.6 17.6 78.2 18.3 C 78.4 18.6 78.8 19 79 19.1 L 79.4 19.4 79.6 19.4 C 79.9 19.4 81.3 18.8 81.9 18.5 C 82.2 18.3 82.6 18.1 82.9 18 C 83.1 17.9 83.5 17.7 83.7 17.5 C 83.9 17.4 84.4 17.2 84.7 17 L 85.2 16.8 85.3 16.6 L 85.5 16.4 85.5 16.1 L 85.5 15.7 85.1 15.1 C 84.2 13.6 82.4 11.7 81.2 11 C 81 10.8 80.7 10.6 80.5 10.5 C 80.3 10.3 79.9 10.1 79.6 10 C 79.3 9.9 78.9 9.7 78.7 9.5 L 78.2 9.2 76.6 8.8 C 75.7 8.6 74.9 8.4 74.8 8.3 L 74.5 8.2 72.8 8.2 C 71.9 8.2 71 8.2 70.7 8.2 M 90.1 8.4 C 89.8 8.5 89.4 8.9 89.2 9.1 L 89.2 9.3 89.2 23.4 L 89.2 37.5 89.3 37.7 C 89.3 37.8 89.5 37.9 89.7 38 L 90 38.1 100.3 38.1 L 110.6 38.2 111 38 L 111.5 37.8 111.6 37.6 L 111.8 37.4 111.8 34.9 L 111.7 32.5 111.6 32.3 C 111.6 32.2 111.3 32 111.1 31.9 L 110.8 31.7 104.1 31.7 L 97.4 31.7 97.2 31.5 C 97.1 31.3 97 31.1 96.9 30.9 L 96.8 30.6 96.8 29 L 96.8 27.4 97.1 27.2 L 97.3 26.9 103.9 26.9 L 110.4 26.9 110.8 26.8 C 111.2 26.7 111.5 26.5 111.6 26.1 L 111.8 25.8 111.8 23.3 L 111.8 20.7 111.7 20.6 C 111.5 20.3 111.3 20.2 110.9 20 L 110.6 19.9 104 19.9 L 97.4 19.9 97.2 19.8 C 97.1 19.8 97 19.6 97 19.5 L 96.8 19.2 96.8 17.5 L 96.8 15.8 97 15.5 C 97 15.4 97.1 15.2 97.2 15.2 L 97.4 15.1 104.1 15.1 L 110.8 15.1 111.1 14.9 L 111.5 14.8 111.6 14.5 L 111.7 14.3 111.8 11.9 L 111.8 9.4 111.6 9.2 L 111.5 8.9 111 8.6 L 110.6 8.4 100.5 8.4 L 90.4 8.3 90.1 8.4 M 115.5 8.4 C 115.3 8.5 115 8.8 114.9 9.2 L 114.8 9.5 114.8 23.4 L 114.8 37.4 114.9 37.6 C 115 37.7 115.2 37.9 115.3 37.9 L 115.4 38 126 38 L 136.6 38 136.8 37.8 C 137.2 37.6 137.2 37.5 137.2 34.8 L 137.2 32.4 137.1 32.1 C 137 32 136.8 31.8 136.7 31.7 L 136.5 31.5 129.9 31.5 L 123.3 31.5 123 31.3 L 122.6 31.1 122.5 30.8 L 122.4 30.5 122.4 20.2 L 122.5 9.9 122.4 9.5 L 122.3 9.1 122 8.8 C 121.9 8.7 121.7 8.5 121.6 8.5 L 121.3 8.3 118.5 8.3 L 115.7 8.3 115.5 8.4 M 141.1 8.5 C 140.7 8.6 140.4 8.8 140.2 9.1 L 140.2 9.2 140.2 23.3 L 140.2 37.4 140.2 37.6 L 140.3 37.7 140.7 37.9 L 141 38 151.4 38 L 161.8 38 162.2 37.8 C 162.4 37.8 162.6 37.6 162.7 37.5 L 162.7 37.4 162.8 34.9 L 162.8 32.4 162.6 32.2 L 162.5 31.9 162.1 31.7 L 161.7 31.5 155.1 31.5 L 148.6 31.5 148.4 31.4 C 148.3 31.3 148.1 31.1 148 31 L 147.9 30.8 147.9 19.9 L 147.9 9.1 147.6 8.8 L 147.3 8.5 146.9 8.4 L 146.6 8.3 144 8.3 L 141.4 8.3 141.1 8.5 M 32.5 8.7 C 32.2 8.7 31.9 9 31.7 9.3 L 31.6 9.5 31.6 11.9 L 31.6 14.3 31.7 14.6 L 31.9 14.9 32.1 15 L 32.4 15.2 35.5 15.2 L 38.5 15.2 38.8 15.3 L 39 15.4 39.1 15.7 L 39.3 16 39.2 26.8 L 39.2 37.5 39.5 37.8 C 39.6 37.9 39.9 38 40.1 38.1 L 40.4 38.2 43.4 38.2 L 46.3 38.2 46.6 38 C 46.8 38 46.9 37.8 47 37.7 L 47.1 37.5 47.1 36.9 L 47.1 36.3 47.3 36 L 47.6 35.7 50 35.7 L 52.4 35.6 52.6 35.5 C 52.7 35.4 52.8 35.2 52.9 35 L 53 34.8 53 33.6 L 53 32.5 52.9 32.2 C 52.8 32.1 52.6 31.8 52.5 31.7 C 52.2 31.4 52.2 31.3 52.4 31 C 52.6 30.9 52.7 30.7 52.8 30.5 L 52.9 30.2 53 29.1 L 53 28 52.9 27.7 L 52.8 27.4 52.5 27.3 L 52.3 27.1 50 27.1 L 47.7 27.1 47.5 27 C 47.3 26.9 47.2 26.7 47.2 26.6 L 47.1 26.4 47.1 21.3 L 47.1 16.2 47.2 15.9 C 47.2 15.7 47.3 15.5 47.4 15.5 L 47.6 15.3 50.8 15.2 L 54 15.2 54.3 15.1 C 54.4 15 54.6 14.8 54.7 14.7 L 54.8 14.5 54.8 12 L 54.9 9.4 54.7 9.2 C 54.6 9.1 54.4 8.9 54.3 8.8 L 54 8.7 51.4 8.7 C 49.9 8.7 47.9 8.7 47 8.7 C 45.1 8.7 42.5 8.7 38.6 8.7 C 37.2 8.7 35.5 8.7 34.8 8.7 C 34 8.7 33.2 8.7 33 8.7 L 32.7 8.6 32.5 8.7 M 48.5 30 C 48.2 30.1 47.9 30.5 47.7 30.9 L 47.7 31.2 47.7 31.6 L 47.8 31.9 48.1 32.2 C 48.3 32.4 48.5 32.6 48.7 32.7 L 48.9 32.9 49.3 32.8 L 49.6 32.8 50.1 32.4 C 50.3 32.2 50.7 31.9 50.9 31.8 C 51.1 31.8 51.3 31.6 51.3 31.6 L 51.4 31.4 51.4 31.3 C 51.3 31.1 50.2 30.2 49.8 30 L 49.5 29.8 49.2 29.8 C 49 29.8 48.7 29.9 48.5 30"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  )
}

/** ROBOS 로고 — 와이어프레임 `LOGO`(1076행). 제품 표기라 번역 대상이 아니다. */
export function BrandLogo() {
  return (
    <svg className="logo" viewBox="0 0 319.61 44.12" aria-hidden="true" focusable="false">
      <g fill="currentColor">
        <path d="M50.11.02H11.02L0,11.04v33.07h11.02V11.04h33.07v5.53l-11.02,10.98v16.55h22.04l-11.02-11.02v-.03l10.99-10.99h.03V5.02C55.11,2.26,52.87.02,50.11.02Z" />
        <path d="M116.24.02h-39.08l-11.02,11.02v33.07h50.11c2.76,0,5-2.24,5-5V5.02c0-2.76-2.24-5-5-5ZM110.22,33.32h-33.07V10.8h33.07v22.52Z" />
        <path d="M187.37,5.02c0-2.76-2.24-5-5-5h-39.08l-11.02,11.02v33.07h50.11c2.76,0,5-2.24,5-5v-6.02h0l-11.02-11.02,11.02-11.02v-6.02ZM171.97,33.32h-28.69V10.8h28.69l-11.02,11.26,11.02,11.26h0Z" />
        <path d="M319.61,10.8v-5.13c0-3.13-2.54-5.67-5.67-5.67h-38.42s-11.02,11.03-11.02,11.03h0s0,0,0,0v5.5h0v10.92h44.09v5.86h-44.09v10.8h49.44c3.13,0,5.67-2.54,5.67-5.67v-5.37h0v-5.5h0v-5.25s0-.01,0-.02v-.13h0c-.08-3.06-2.58-5.52-5.66-5.52h-38.42v-5.86h44.09Z" />
        <path d="M248.48.02h-39.08l-11.02,11.02v33.07h50.11c2.76,0,5-2.24,5-5V5.02c0-2.76-2.24-5-5-5ZM242.47,33.32h-33.07V10.8h33.07v22.52Z" />
      </g>
    </svg>
  )
}
